import {List} from 'immutable';
import {defineTypeReferenceForReferenceField} from './type-references';
import {
  isNameField,
  isSingleRef,
  isConfigurableField,
  isItemRef,
  isItemRefSet,
  itemRefTargetCollectionId,
} from '@packages/systems/dynamo/utils/CollectionFieldUtils';
import {naturalCmp as compareAlphabetical} from '@packages/utilities/string-utils';
import {fieldSlug as formatFieldSlug} from '@packages/systems/dynamo/utils/SlugUtils';

import type {
  BindablePropertyDef,
  BindingPath,
  TypeReferenceBuilder,
  GeneralFieldType,
} from '@packages/systems/dynamo/source-types';
import type {CollectionRecordType as Collection} from '@packages/systems/dynamo/records/CollectionRecord';
import type {FieldType as Field} from '@packages/systems/dynamo/records/Field';
import type {
  CMSCollectionFieldType,
  CMSCollectionSetFieldType,
} from '@packages/systems/dynamo/config';

export type FieldPath = [Field] | [Field, Field] | [Field, Field, Field];

/*
  Returns fields of a collection that should be available as binding options.

  These should follow CMS business logic and include all default and custom
  fields with a bindable field type. Some default fields, like draft and
  archived, are left out because these contain bookkeeping information that
  is internal to the CMS.

  WARNING: This method should only handle use cases that apply to _all_ CMS
  or CMS-like collections. Any gateway-specific edge cases should be handled
  at a callsite for this function.
*/
export function getBindableFields(collection: Collection): Field[] {
  return collection.fields.reduce<Field[]>((fields, field) => {
    if (field && isBindableField(field)) {
      fields.push(field);
    }
    return fields;
  }, []);
}

function isBindableField(field: Field) {
  if (NOT_BINDABLE_DEFAULT_SLUGS.includes(field.slug)) {
    return false;
  }

  if (field.type === 'Set') {
    return field.innerType === 'ImageRef';
  }

  return BINDABLE_FIELD_TYPES.includes(field.type);
}

// slugs of
const NOT_BINDABLE_DEFAULT_SLUGS = [
  '_draft',
  '_archived',
  '_noSearch',
  'created-by',
  'published-by',
  'updated-by',
];

const BINDABLE_FIELD_TYPES = [
  'PlainText',
  'RichText',
  'Number',
  'Date',
  'Option',
  'ImageRef',
  'Video',
  'Link',
  'Email',
  'Phone',
  'FileRef',
  'Bool',
  'Color',
  'ItemRef',
  'ItemRefSet',
  'CommercePrice',
  'HighlightedText',
];

export function defineBindablePropertyFromField<
  RootTRef,
  EntityTRef,
  ListTRef,
>({
  collection,
  field,
  referenceBuilder,
  groupLabel,
  renderingStrategy,
}: {
  collection: Collection;
  field: Field;
  referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>;
  groupLabel?: string;
  renderingStrategy?: 'server' | 'client';
}): BindablePropertyDef<EntityTRef, ListTRef> | null {
  return defineBindablePropertyFromFieldPath<RootTRef, EntityTRef, ListTRef>({
    collection,
    fieldPath: [field],
    referenceBuilder,
    groupLabel,
    renderingStrategy,
  });
}

function getDateFormat(field: Field): 'date' | 'date-time' {
  switch (field.slug) {
    case 'created-on':
    case 'updated-on':
    case 'published-on': {
      return 'date-time';
    }
    default: {
      const format = field.getIn(['validations', 'format']);
      return format === 'date-time' ? format : 'date';
    }
  }
}

export function defineBindablePropertyFromFieldPath<
  RootTRef,
  EntityTRef,
  ListTRef,
>({
  collection,
  fieldPath,
  referenceBuilder,
  groupLabel,
  renderingStrategy,
  queryPath: queryPathOverride,
  deprecated,
}: {
  collection?: Collection | null;
  fieldPath: FieldPath;
  referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>;
  groupLabel?: string;
  renderingStrategy?: 'server' | 'client';
  queryPath?: string[];
  deprecated?: boolean;
}): BindablePropertyDef<EntityTRef, ListTRef> | null {
  const field = fieldPath[fieldPath.length - 1];
  const queryPath = queryPathOverride ?? toQueryPath(fieldPath);
  if (!field || !queryPath) {
    return null;
  }

  const base: {
    id: BindablePropertyDef<EntityTRef, ListTRef>['id'];
    label: BindablePropertyDef<EntityTRef, ListTRef>['label'];
    path: BindablePropertyDef<EntityTRef, ListTRef>['path'];
    queryPath: BindablePropertyDef<EntityTRef, ListTRef>['queryPath'];
    rendering?: BindablePropertyDef<EntityTRef, ListTRef>['rendering'];
    groupLabel?: BindablePropertyDef<EntityTRef, ListTRef>['groupLabel'];
    deprecated?: BindablePropertyDef<EntityTRef, ListTRef>['deprecated'];
  } = {
    id: field.id,
    label: field.name,
    path: toBindingPath(fieldPath),
    queryPath,
  };

  if (groupLabel) {
    base.groupLabel = groupLabel;
  }

  if (renderingStrategy) {
    base.rendering = {strategy: renderingStrategy};
  }

  if (deprecated) {
    base.deprecated = true;
  }

  const fieldType = field.type;

  if (field.slug === 'slug') {
    const collectionId = collection?._id;

    if (!collectionId) {
      return null;
    }

    // map built-in slug fields to the special Slug type
    return {
      type: 'Slug',
      meta: {
        required: field.required,
        legacyTemplatePageCollectionId: collectionId,
      },
      ...base,
    };
  }

  switch (fieldType) {
    case 'PlainText': {
      return {
        type: 'PlainText',
        meta: {
          singleLine: getValidationBooleanValue(field, 'singleLine', true),
          minLength: getValidationNumberValue(field, 'minLength'),
          maxLength: getValidationNumberValue(field, 'maxLength'),
          required: field.required,
        },
        ...base,
      };
    }
    case 'RichText': {
      return {
        type: 'RichText',
        meta: {
          minLength: getValidationNumberValue(field, 'minLength'),
          maxLength: getValidationNumberValue(field, 'maxLength'),
          required: field.required,
        },
        ...base,
      };
    }
    case 'Number': {
      const format = field.getIn(['validations', 'format']);

      return {
        type: 'Number',
        meta: {
          format: format === 'decimal' || format === 'integer' ? format : 'any',
          precision: getValidationNumberValue(field, 'precision'),
          allowNegative: getValidationBooleanValue(
            field,
            'allowNegative',
            false
          ),
          minValue: getValidationNumberValue(field, 'minValue'),
          maxValue: getValidationNumberValue(field, 'maxValue'),
          required: field.required,
        },
        ...base,
      };
    }
    case 'Date': {
      return {
        type: 'Date',
        meta: {
          format: getDateFormat(field),
          required: field.required,
        },
        ...base,
      };
    }

    case 'Option': {
      const options = field.getIn(['validations', 'options'], List());

      return {
        type: 'Option',
        meta: {
          // @ts-expect-error - TS18046 - 'options' is of type 'unknown'.
          options: options
            // @ts-expect-error - TS7006 - Parameter 'option' implicitly has an 'any' type.
            .map((option) => ({
              id: option.get('id', ''),
              name: option.get('name', ''),
            }))
            .toArray(),
          required: field.required,
        },
        ...base,
      };
    }
    case 'ItemRef': {
      const typeReferenceDef = defineTypeReferenceForReferenceField(field);

      if (!typeReferenceDef) {
        return null;
      }

      const type = referenceBuilder.entityOf(typeReferenceDef);
      const legacyReferencedCollectionId = itemRefTargetCollectionId(field);
      const required = field.required;

      const meta = legacyReferencedCollectionId
        ? {type, required, legacyReferencedCollectionId}
        : {type, required};

      return {type: 'ItemRef', meta, ...base};
    }
    case 'ItemRefSet': {
      const typeReferenceDef = defineTypeReferenceForReferenceField(field);

      if (!typeReferenceDef) {
        return null;
      }

      const type = referenceBuilder.listOf(typeReferenceDef);
      const legacyReferencedCollectionId = itemRefTargetCollectionId(field);
      const required = field.required;

      const meta = legacyReferencedCollectionId
        ? {type, required, legacyReferencedCollectionId}
        : {type, required};

      return {type: 'ItemRefSet', meta, ...base};
    }
    case 'Set': {
      const innerType = field.innerType;

      if (innerType !== 'ImageRef') {
        return null;
      }

      return {
        type: 'Set',
        meta: {
          innerType,
          required: field.required,
        },
        ...base,
      };
    }
    case 'ImageRef':
    case 'Video':
    case 'Link':
    case 'Email':
    case 'Phone':
    case 'FileRef':
    case 'Bool':
    case 'Color':
    case 'CommercePrice': {
      return {
        type: fieldType as GeneralFieldType,
        meta: {
          required: field.required,
        },
        ...base,
      };
    }
    default: {
      return null;
    }
  }
}

export function toBindingPath(fieldPath: FieldPath): BindingPath {
  return fieldPath.map((field) => {
    const {id, slug, type, innerType} = field;
    return innerType ? {id, slug, type, innerType} : {id, slug, type};
  });
}

export const NON_CUSTOM_FIELDS_TYPES = new Set(['name', 'slug']);

/**
 * Mapping of field types to their corresponding GraphQL resolvers.
 */
export const CUSTOM_FIELDS_TO_QUERY_MAP: Record<string, string> = {
  Bool: 'fieldBool',
  Color: 'fieldColor',
  CommercePrice: 'fieldCommercePrice',
  CommercePropTable: 'fieldCommercePropTable',
  CommercePropValues: 'fieldCommercePropValues',
  Email: 'fieldEmail',
  EnumOption: 'fieldEnumOption',
  ExtFileRef: 'fieldExtFileRef',
  FileRef: 'fieldFileRef',
  ImageRef: 'fieldImageRef',
  ItemRef: 'resolveLocalItemRef',
  ItemRefSet: 'resolveLocalItemRefSet',
  Link: 'fieldLink',
  Number: 'fieldNumber',
  Option: 'fieldOption',
  Phone: 'fieldPhone',
  PlainText: 'fieldPlainText',
  RichText: 'fieldRichText',
  SubscriptionPlan: 'fieldSubscriptionPlan',
  User: 'fieldUser',
  Video: 'fieldVideo',
} satisfies Record<
  Exclude<
    CMSCollectionFieldType,
    /**
     * - `Set` is handled by `CUSTOM_FIELDS_TO_QUERY_MAP_WITH_INNER_TYPE`
     * - `Password` is not supported
     * - `Image` is handled by `ImageRef`
     * - `Date` is handled via logic in `formatGQLField` since it can be
     *    either `fieldDate` or `fieldDateTime`
     */
    'Set' | 'Password' | 'Image' | 'Date'
  >,
  string
>;

const CUSTOM_FIELDS_TO_QUERY_MAP_WITH_INNER_TYPE: Record<string, string> = {
  ImageRef: 'fieldSetOfImageRef',
  ExtFileRef: 'fieldSetOfExtFileRef',
} satisfies Record<CMSCollectionSetFieldType, string>;

export function formatGQLField(field: Field): string | null {
  const {slug, type: fieldType} = field;
  if (NON_CUSTOM_FIELDS_TYPES.has(slug)) {
    return slug;
  }

  let queryField: string | undefined;
  if (fieldType === 'Set' && field.innerType) {
    queryField = CUSTOM_FIELDS_TO_QUERY_MAP_WITH_INNER_TYPE[field.innerType];
  } else if (fieldType === 'Date') {
    const format = getDateFormat(field);
    queryField = format === 'date-time' ? 'fieldDateTime' : 'fieldDate';
  } else {
    queryField = CUSTOM_FIELDS_TO_QUERY_MAP[fieldType];
  }

  return queryField ? `${queryField}(slug: "${slug}")` : null;
}

function toQueryPath(fieldPath: FieldPath): string[] | null {
  const queryPath: string[] = [];

  for (const field of fieldPath) {
    const alias = formatFieldSlug(field.slug);
    const fieldArgs = formatGQLField(field);

    if (!fieldArgs) {
      return null;
    }

    queryPath.push(`${alias}: ${fieldArgs}`);
  }

  return queryPath;
}

export const sortFields = (fields: Field[]) => {
  const copy = [...fields];
  copy.sort(compareFields);
  return copy;
};

export function compareFields(fieldA: Field, fieldB: Field) {
  // The name of the root collection always goes first
  if (isNameField(fieldA) !== isNameField(fieldB)) {
    return isNameField(fieldA) ? -1 : 1;
  }

  // Followed by all direct custom-user defined, non-reference fields
  if (
    _isSimpleConfigurableField(fieldA) !== _isSimpleConfigurableField(fieldB)
  ) {
    return _isSimpleConfigurableField(fieldA) ? -1 : 1;
  }

  // Followed by all ItemRef expansions
  if (isSingleRef(fieldA) !== isSingleRef(fieldB)) {
    return isSingleRef(fieldA) ? -1 : 1;
  }

  // Then by the ItemRefSet fields
  if (isItemRefSet(fieldA) !== isItemRefSet(fieldB)) {
    return isItemRefSet(fieldA) ? -1 : 1;
  }

  // If all else is the same, sort alphabetically
  return compareAlphabetical(fieldA.name, fieldB.name);
}

function _isSimpleConfigurableField(field: Field) {
  return isConfigurableField(field) && !isItemRef(field);
}

function getValidationBooleanValue(
  field: Field,
  validationKey: string,
  notFoundValue: boolean
): boolean {
  const rawValidationValue = field.getIn(['validations', validationKey]);
  return typeof rawValidationValue === 'boolean'
    ? rawValidationValue
    : notFoundValue;
}

function getValidationNumberValue(
  field: Field,
  validationKey: string
): number | undefined {
  const rawValidationValue = field.getIn(['validations', validationKey]);

  if (typeof rawValidationValue === 'number') {
    return rawValidationValue;
  }

  if (typeof rawValidationValue === 'string') {
    const integerOrNan = parseInt(rawValidationValue, 10);

    if (!Number.isNaN(integerOrNan)) {
      return integerOrNan;
    }
  }

  return undefined;
}
